import {NetworkStatus, useApolloClient} from '@apollo/client';
import {
  useAllAccountsQuery,
  useAllRawTransactionsLazyQuery,
} from '@transactions/graphql';
import {parseDate} from 'common/date';
import * as React from 'react';
import updateRawTransactions from 'src/apolloCache/updateRawTransactions';
import {AccountFilterSelect} from 'src/components/AccountFilterSelect';
import {
  AmountFilterSelect,
  amountArg,
  normalizeAmountBounds,
  parseAmountParam,
} from 'src/components/AmountFilterSelect';
import {DateFilterSelect} from 'src/components/DateFilterSelect';
import {FilterPillRow} from 'src/components/FilterPillRow';
import {useUrlFilterParams} from 'src/hooks/useUrlFilterParams';
import Empty from 'src/ListView/Empty';
import {MoreButton} from 'src/ListView/MoreButton';
import Loading from 'src/Loading';
import RawTransactionList from 'src/RawTransactionList';
import {
  AllocationFilterSelect,
  parseAllocationFilter,
} from 'src/RawTransactionListView/AllocationFilterSelect';
import {Header} from 'src/RawTransactionListView/Header';
import {
  parseReconciledFilter,
  ReconciledFilterSelect,
  reconciledArg,
} from 'src/RawTransactionListView/ReconciledFilterSelect';
import {useRawRowGestures} from 'src/RawTransactionListView/useRawRowGestures';
import {useRawSelectionActions} from 'src/RawTransactionListView/useRawSelectionActions';
import {ScrollTable} from 'src/ScrollTable';
import {SearchInput} from 'src/SearchInput';
import {useSelectionState} from 'src/useSelectionState';
import type {
  AllRawTransactionsQuery,
  BasicRawTransactionFragment,
} from '@transactions/graphql';

const ACTIVITY_LIMIT = 25;

// "2026-03-01" -> "Mar 1, 2026", for naming the edge the list pages past.
const formatBound = (date: string) => parseDate(date).toFormat('MMM d, yyyy');

/**
 * Marks where the list leaves the date filter's window. The rows under it were
 * reached by paging past a bound, not by the filter — so a list that runs
 * wider than the pill above it says so, at the row where it starts.
 */
const OutsideWindowDivider = ({label}: {label: string}) => (
  // A separator rather than a plain div: it divides the list into the window
  // and what lies outside it, and the role is also what tells it apart from
  // the paging control of the same name at that end.
  <div
    role="separator"
    aria-label={label}
    className="text-muted-foreground border-border bg-muted/40 border-y px-4 py-1.5 text-xs font-medium tracking-wide uppercase"
  >
    {label}
  </div>
);

/**
 * Top-level cross-account activity browser: every account's raw transactions
 * (newest first, processed included) in one paginated list, so mirrored or
 * duplicate movement between two accounts is visible without bouncing between
 * per-account Transactions tabs. Filters narrow by account (multi-select),
 * date, amount magnitude, description search, allocation status, and
 * reconciliation status.
 *
 * The Ledger's Activity layer ("All"), which owns the page's breadcrumb.
 *
 * The date filter bounds the list's *starting window*, not the list: when a
 * direction runs out, the footer (older) and a matching header (newer) offer
 * to keep paging straight past the bound, so following a thread out of the
 * month you filtered to never costs a round trip through the filter menu.
 */
export const ActivityPage = () => {
  const client = useApolloClient();

  const {data: accountsData} = useAllAccountsQuery();
  const accounts = React.useMemo(
    () => accountsData?.allAccounts ?? [],
    [accountsData],
  );

  // Filters live in the URL query string so they survive navigation: opening a
  // transaction and hitting back returns to the same filtered activity list.
  //
  // Each change is *pushed* rather than replaced, so the filters themselves
  // are navigable: browsing here is exploratory — narrow to an account, add a
  // date window, then a magnitude floor — and back is how you undo a step you
  // didn't mean to take, instead of rebuilding the previous view from the
  // menus. Opening a row and coming back still lands on the filters you left.
  const [searchParams, setParams] = useUrlFilterParams({history: 'push'});
  const accountIds = React.useMemo(() => {
    const raw = searchParams.get('accounts');
    return raw ? raw.split(',') : [];
  }, [searchParams]);
  const allocationFilter = parseAllocationFilter(searchParams.get('allocation'));
  const reconciledFilter = parseReconciledFilter(searchParams.get('reconciled'));
  const dateFilter = {
    startDate: searchParams.get('startDate'),
    endDate: searchParams.get('endDate'),
  };
  // Magnitude bounds, in dollars in the URL and cents on the query. A
  // hand-edited or shared URL can carry the pair backwards, which the server
  // rejects outright — swap it (as TransactionFilters does) rather than let
  // the whole page fall into its load-error state.
  const amountFilter = normalizeAmountBounds({
    minAmount: parseAmountParam(searchParams.get('minAmount')),
    maxAmount: parseAmountParam(searchParams.get('maxAmount')),
  });
  const search = searchParams.get('search') ?? '';

  // Every key the filter row owns, so Reset can clear them in one history
  // entry (and so "is anything filtered?" is one list, not seven conditions
  // that drift apart as filters are added).
  const activeFilterKeys = [
    accountIds.length > 0 ? 'accounts' : null,
    allocationFilter !== 'all' ? 'allocation' : null,
    reconciledFilter !== 'all' ? 'reconciled' : null,
    dateFilter.startDate != null ? 'startDate' : null,
    dateFilter.endDate != null ? 'endDate' : null,
    amountFilter.minAmount != null ? 'minAmount' : null,
    amountFilter.maxAmount != null ? 'maxAmount' : null,
    search !== '' ? 'search' : null,
  ].filter((key): key is string => key != null);

  const resetFilters = () => {
    setParams(
      Object.fromEntries(activeFilterKeys.map((key) => [key, null])),
    );
  };

  const filters = {
    includeProcessed: true,
    // The server unions accountId + accountIds; passing an empty array would
    // still narrow to nothing, so omit it entirely for the all-accounts view.
    accountIds: accountIds.length > 0 ? accountIds : undefined,
    allocated:
      allocationFilter === 'allocated'
        ? true
        : allocationFilter === 'unallocated'
          ? false
          : undefined,
    reconciled: reconciledArg(reconciledFilter),
    startDate: dateFilter.startDate,
    endDate: dateFilter.endDate,
    minAmount: amountArg(amountFilter.minAmount),
    maxAmount: amountArg(amountFilter.maxAmount),
    search: search || undefined,
    limit: ACTIVITY_LIMIT,
  };

  const [
    loadRawTransactions,
    {loading, error, data, fetchMore, networkStatus},
  ] = useAllRawTransactionsLazyQuery({notifyOnNetworkStatusChange: true});

  // This page's edge has been carried past that bound: a request was issued
  // for rows outside it, so the edge is no longer the filter's. It records
  // the *edge*, not the rows — outside rows can be deleted while the cursor
  // stays where the extension left it, and a later page must still be
  // requested unbounded or it would ask the server for rows older than a
  // cursor that is already older than the bound, and get nothing.
  //
  // Cleared only by `resetPagingState`, so it lives exactly as long as the
  // page it describes: a filter change or a base refetch replaces that page
  // and the fact retires with it. Reset on a failed request too, or a list
  // that never extended would look exhausted with no way to retry.
  const [extendedPastStart, setExtendedPastStart] = React.useState(false);
  const [extendedPastEnd, setExtendedPastEnd] = React.useState(false);
  // `hasMore` on the cached page answers for the older end only, so the newer
  // end keeps its own flag and spinner. Null is *unknown* rather than false:
  // only a fetch from that end can answer, and a page restored from the cache
  // on a return visit has never made one.
  const [hasNewer, setHasNewer] = React.useState<boolean | null>(null);
  // The scope whose newer request is in flight, rather than a bare flag. A
  // request outstanding when the filters change would otherwise keep the new
  // scope's spinner up and its control unclickable until the old one settled
  // — indefinitely, if it never does. Reading it as "mine" makes a stale
  // request invisible here the moment the scope changes.
  const [loadingNewerScope, setLoadingNewerScope] = React.useState<
    string | null
  >(null);
  // Advanced whenever the cached page is replaced, so a request issued
  // against the old one can be told apart from one issued against the new.
  const [pageGeneration, setPageGeneration] = React.useState(0);

  const all = React.useMemo(
    () => data?.allRawTransactions?.items ?? [],
    [data],
  );

  // `hasMore` and `endCursor` describe the older end of the cached page;
  // `startCursor` the newer end.
  const hasMore = data?.allRawTransactions?.hasMore ?? false;
  const endCursor = data?.allRawTransactions?.endCursor ?? null;
  const startCursor = data?.allRawTransactions?.startCursor ?? null;

  // Rows outside the filter's window, split off so each run can be introduced
  // by the bound it sits past. Sorted newest first, they can only be at the
  // ends — but the split is by date rather than by position, so a cache patch
  // that lands a row mid-list can't shift the boundary onto the wrong row.
  const {startDate, endDate} = dateFilter;
  const [newerRows, windowRows, olderRows] = React.useMemo(
    () => [
      endDate ? all.filter((rt) => rt.date > endDate) : [],
      all.filter(
        (rt) =>
          !(endDate && rt.date > endDate) && !(startDate && rt.date < startDate),
      ),
      startDate ? all.filter((rt) => rt.date < startDate) : [],
    ],
    [all, startDate, endDate],
  );

  // Whether the loaded list already runs past each bound — read from the rows
  // first and the click second. Apollo answers a remount from its cache, so
  // coming back to a list left extended restores the extended page; deriving
  // this from the rows recognizes that page for what it is, where a flag
  // reset on mount would send the next request back inside the bound and read
  // its empty answer as the end of the list.
  const pagedPastStart = olderRows.length > 0 || extendedPastStart;
  const pagedPastEnd = newerRows.length > 0 || extendedPastEnd;

  const handleUpdateRawTransactions = updateRawTransactions({
    client,
    filters,
    // Rows a mutation creates are kept out of the cached page when they fall
    // outside the filters — but a page extended past a date bound is wider
    // than the variables that keyed it, so the check uses the window the list
    // is actually showing.
    matchFilters:
      pagedPastStart || pagedPastEnd
        ? {
            ...filters,
            startDate: pagedPastStart ? null : filters.startDate,
            endDate: pagedPastEnd ? null : filters.endDate,
          }
        : undefined,
  });

  const selectionState = useSelectionState<BasicRawTransactionFragment>(
    all,
    [],
  );
  const {clearSelection} = selectionState;
  const selectionActions = useRawSelectionActions({
    selectionState,
    onUpdate: handleUpdateRawTransactions,
    includeProcessed: true,
    // The Unallocated view excludes consumed rows server-side, so a row
    // consumed by a mutation must drop from the cached page too.
    removeFullyConsumed: allocationFilter === 'unallocated',
  });
  const gestures = useRawRowGestures({
    selectionState,
    openCreate: selectionActions.openCreateForRow,
  });

  // Page from the query's own cursors (surviving cache removals), guarded so a
  // repeated call can't append the same page twice. One guard per direction:
  // the two ends advance independently.
  const lastOlderCursor = React.useRef<string | null>(null);
  const lastNewerCursor = React.useRef<string | null>(null);

  // Everything the page holds *about the cached page* rather than about the
  // list: the guards against re-requesting a cursor, the exhausted-bound
  // claims, and the newer end's answer. It all describes one page, so it all
  // retires together — whichever way that page is replaced. Keeping it in one
  // place is the point: when the claims and the guards were reset separately,
  // a path that cleared one and not the other left the control offered and
  // inert, which is worse than not offering it.
  const resetPagingState = () => {
    // Retires every request issued against the page being replaced: their
    // answers describe rows that are no longer on screen.
    setPageGeneration((generation) => generation + 1);
    lastOlderCursor.current = null;
    lastNewerCursor.current = null;
    setExtendedPastStart(false);
    setExtendedPastEnd(false);
    setHasNewer(null);
  };

  const handleReload = () => {
    clearSelection();
    resetPagingState();
    void loadRawTransactions({variables: filters});
  };
  React.useEffect(handleReload, [
    accountIds.join(','),
    allocationFilter,
    reconciledFilter,
    dateFilter.startDate,
    dateFilter.endDate,
    amountFilter.minAmount,
    amountFilter.maxAmount,
    search,
  ]);

  // A refetch of the base query answers with a fresh bounded page, so a "that
  // bound is exhausted" claim recorded against the old one is a claim about
  // rows the server has just been asked about again. Cursor equality can't
  // see this: a sync that imports only rows *outside* the window returns the
  // same in-window page, and with it the same cursor, so the claim would
  // survive the very event most likely to have falsified it.
  //
  // Clearing is the conservative direction. The claim only ever hides a
  // control, so dropping it costs one request if the bound really is still
  // exhausted, where keeping it wrongly leaves the newly imported rows
  // unreachable until the filter changes.
  React.useEffect(() => {
    if (networkStatus !== NetworkStatus.refetch) return;
    resetPagingState();
  }, [networkStatus]);

  const loadedIds = (previous: AllRawTransactionsQuery) =>
    new Set((previous.allRawTransactions?.items ?? []).map((rt) => rt._id));

  // A merged page can't just be concatenated at the end it came from. A cache
  // mutation can move a row past the cursor the fetch was issued against — an
  // added or edited row newer than `startCursor` sorts to the front while the
  // cursor stays put — and the rows that come back are then not all newer
  // than everything already loaded. Sorting the merge is what keeps the list
  // newest-first regardless, and matches what `updateRawTransactions` does to
  // the same list.
  const sortNewestFirst = (items: BasicRawTransactionFragment[]) =>
    [...items].sort((a, b) => b.date.localeCompare(a.date));

  // Identifies the page a request was issued against. A fetch is answered by
  // whatever the observable query is watching *now*, so one still in flight
  // when the page changes underneath it would merge rows into a page they
  // don't belong to.
  //
  // The filters alone can't identify it: a refetch replaces the page without
  // changing them, so a newer fetch issued before a sync could land after it
  // and re-establish, from an answer computed against the old rows, exactly
  // the exhausted-bound state `resetPagingState` had just cleared. The
  // generation is what distinguishes those two pages — it advances whenever
  // the paging state retires, which is precisely when an outstanding request
  // stops describing what is on screen.
  //
  // Mirrored into a ref because the comparison happens in an async callback,
  // where the render's value would be the one captured at request time.
  const scopeToken = `${JSON.stringify(filters)}#${pageGeneration}`;
  const scopeTokenRef = React.useRef(scopeToken);
  scopeTokenRef.current = scopeToken;

  // Appends the page of rows older than `cursor`. `unbounded` drops the
  // filter's start date, which is what paging past that bound means.
  const fetchOlder = (cursor: string, unbounded: boolean) => {
    lastOlderCursor.current = cursor;
    const scope = scopeToken;
    void fetchMore({
      variables: {
        ...filters,
        startDate: unbounded ? undefined : filters.startDate,
        beforeCursor: cursor,
      },
      updateQuery: (previous: AllRawTransactionsQuery, result) => {
        // Answered after the filters moved on: these rows were selected by a
        // scope the page no longer shows, so drop them rather than merge them
        // into a page they don't belong to.
        if (scopeTokenRef.current !== scope) return previous;
        const page = result.fetchMoreResult?.allRawTransactions;
        const existingIds = loadedIds(previous);
        const moreItems = (page?.items ?? []).filter(
          (rt) => !existingIds.has(rt._id),
        );
        return {
          ...previous,
          allRawTransactions: {
            ...previous.allRawTransactions,
            items: sortNewestFirst([
              ...(previous.allRawTransactions?.items ?? []),
              ...moreItems,
            ]),
            // An empty page carries no cursor; keeping the previous one leaves
            // the guard above able to recognize a repeat of this request.
            endCursor:
              page?.endCursor ?? previous.allRawTransactions?.endCursor ?? null,
            hasMore: page?.hasMore ?? false,
          },
        };
      },
    }).catch(() => {
      if (scopeTokenRef.current !== scope) return;
      lastOlderCursor.current = null;
      // A failed page must not leave the list looking exhausted: put the
      // offer back so the click can be retried. Harmless when an earlier
      // extension landed rows — those keep `pagedPastStart` true on their own.
      if (unbounded) setExtendedPastStart(false);
    });
  };

  // The mirror image, prepending rows newer than `cursor`.
  const fetchNewer = (cursor: string) => {
    lastNewerCursor.current = cursor;
    const scope = scopeToken;
    setLoadingNewerScope(scope);
    void fetchMore({
      variables: {...filters, endDate: undefined, afterCursor: cursor},
      updateQuery: (previous: AllRawTransactionsQuery, result) => {
        if (scopeTokenRef.current !== scope) return previous;
        const page = result.fetchMoreResult?.allRawTransactions;
        const existingIds = loadedIds(previous);
        const moreItems = (page?.items ?? []).filter(
          (rt) => !existingIds.has(rt._id),
        );
        return {
          ...previous,
          allRawTransactions: {
            ...previous.allRawTransactions,
            items: sortNewestFirst([
              ...moreItems,
              ...(previous.allRawTransactions?.items ?? []),
            ]),
            // `hasMore` and `endCursor` belong to the older end of the list; a
            // page fetched from the newer end must not answer for them, or
            // reaching the newest row would read as reaching the oldest.
            startCursor:
              page?.startCursor ??
              previous.allRawTransactions?.startCursor ??
              null,
          },
        };
      },
    })
      .then((result) => {
        // Same as the merge: an answer from a scope the page has left says
        // nothing about the one it's showing, so leave `hasNewer` unknown.
        if (scopeTokenRef.current !== scope) return;
        setHasNewer(result.data?.allRawTransactions?.hasMore ?? false);
      })
      .catch(() => {
        if (scopeTokenRef.current !== scope) return;
        lastNewerCursor.current = null;
        // Same as fetchOlder: a failure returns the control, and `hasNewer`
        // stays unknown rather than answering "no" on the strength of it.
        setExtendedPastEnd(false);
      })
      .finally(() => {
        // Only ever clears its own: a late resolution must not take down a
        // spinner the current scope raised for a request still running.
        setLoadingNewerScope((current) => (current === scope ? null : current));
      });
  };

  // Only the current scope's request shows here; a stale one is invisible.
  const loadingNewer = loadingNewerScope === scopeToken;

  const isEmpty = all.length === 0;

  const handleMore = () => {
    if (loading) return;
    if (hasMore) {
      if (endCursor && lastOlderCursor.current !== endCursor) {
        fetchOlder(endCursor, pagedPastStart);
      }
      return;
    }
    // The window is exhausted: keep going past the filter's older bound.
    if (pagedPastStart || !startDate) return;
    if (!endCursor) {
      // No row to page from — and no scroll position or loaded page to
      // preserve either, so dropping the bound from the filter itself is the
      // same list in one request, with a URL that still says what is being
      // shown. Keyed on the cursor rather than on the row count: a page a
      // mutation emptied keeps its cursor, and paging from it is more precise
      // than widening.
      setParams({startDate: null});
      return;
    }
    setExtendedPastStart(true);
    fetchOlder(endCursor, true);
  };

  const handleMoreNewer = () => {
    if (loading || loadingNewer) return;
    // An empty window has no cursor, and this end has no widening to fall
    // back on — see `canPageNewer`.
    if (!startCursor || lastNewerCursor.current === startCursor) return;
    setExtendedPastEnd(true);
    fetchNewer(startCursor);
  };

  // The older end can be extended once per bound; past it, the cached page's
  // own `hasMore` takes over. The newer end has no such stored answer, so it
  // offers while the window has an end bound and retires only once a fetch
  // from that end has come back empty. It is not gated on the bound being in
  // the past: future-dated rows exist, and a bound is exactly the thing that
  // would hide them.
  //
  // Only the older end is offered on a window with no anchor, which looks
  // lopsided and isn't. With no cursor to page from, the control has to widen
  // the filter instead — and dropping the *start* date leaves an upper bound,
  // so a newest-first page lands on the rows just older than the window,
  // which is what was asked for. Dropping the *end* date leaves no upper
  // bound and lands on the newest rows in the account, which may be years
  // past the window. Reaching the rows just newer than a date needs the
  // server to paginate ascending from it, and `afterCursor` is opaque, so
  // there is nothing honest to anchor to yet (see todo/ledger-paging-edges.md).
  //
  // The test is the cursor, not the row count: a page a mutation emptied
  // keeps its `startCursor`, so it still has a real anchor and the control
  // still works. Only a window that never had rows has nothing to offer.
  const canPageOlder =
    !hasMore && !pagedPastStart && Boolean(startDate) && Boolean(data);
  const canPageNewer =
    startCursor !== null &&
    Boolean(data) &&
    (pagedPastEnd ? hasNewer !== false : Boolean(endDate));

  const rowProps = {
    ...gestures.rowProps,
    showProcessedStatus: true,
  };

  let bodyContent: React.ReactNode;
  if (data) {
    bodyContent = !isEmpty ? (
      <>
        {endDate && newerRows.length > 0 && (
          <>
            <OutsideWindowDivider label={`Newer than ${formatBound(endDate)}`} />
            <RawTransactionList transactions={newerRows} {...rowProps} />
          </>
        )}
        <RawTransactionList transactions={windowRows} {...rowProps} />
        {startDate && olderRows.length > 0 && (
          <>
            <OutsideWindowDivider
              label={`Older than ${formatBound(startDate)}`}
            />
            <RawTransactionList transactions={olderRows} {...rowProps} />
          </>
        )}
      </>
    ) : (
      <Empty>
        {allocationFilter === 'unallocated'
          ? 'Everything is allocated'
          : 'No transactions'}
      </Empty>
    );
  } else if (error) {
    bodyContent = <Empty>Couldn&apos;t load activity.</Empty>;
  } else {
    bodyContent = <Loading size="medium" />;
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex min-h-0 w-full flex-1 flex-col">
        <Header
          selectionState={selectionState}
          actions={selectionActions}
          gestures={gestures}
          showSyncButton={false}
          // The search box rides the action row rather than the filter row: it
          // needs a width the pills don't, and pairing it with the actions
          // keeps the whole header to two rows on a phone.
          extraActions={
            <div className="min-w-0 flex-1 md:w-56 md:flex-none">
              <SearchInput
                initialQuery={search}
                onSearch={(query) => setParams({search: query})}
                inputProps={{placeholder: 'Search'}}
              />
            </div>
          }
          filterContent={
            <FilterPillRow
              pills={[
                {
                  key: 'dates',
                  active:
                    dateFilter.startDate != null || dateFilter.endDate != null,
                  pill: (
                    <DateFilterSelect
                      value={dateFilter}
                      onChange={(filter) =>
                        setParams({
                          startDate: filter.startDate,
                          endDate: filter.endDate,
                        })
                      }
                    />
                  ),
                },
                {
                  key: 'accounts',
                  active: accountIds.length > 0,
                  pill: (
                    <AccountFilterSelect
                      accounts={accounts}
                      value={accountIds}
                      onChange={(ids) => setParams({accounts: ids.join(',')})}
                    />
                  ),
                },
                {
                  key: 'allocation',
                  active: allocationFilter !== 'all',
                  pill: (
                    <AllocationFilterSelect
                      value={allocationFilter}
                      onChange={(filter) =>
                        setParams({
                          allocation: filter === 'all' ? null : filter,
                        })
                      }
                    />
                  ),
                },
                {
                  key: 'reconciled',
                  active: reconciledFilter !== 'all',
                  pill: (
                    <ReconciledFilterSelect
                      value={reconciledFilter}
                      onChange={(filter) =>
                        setParams({
                          reconciled: filter === 'all' ? null : filter,
                        })
                      }
                    />
                  ),
                },
                {
                  key: 'amount',
                  active:
                    amountFilter.minAmount != null ||
                    amountFilter.maxAmount != null,
                  pill: (
                    <AmountFilterSelect
                      value={amountFilter}
                      onChange={({minAmount, maxAmount}) =>
                        setParams({
                          minAmount:
                            minAmount != null ? String(minAmount) : null,
                          maxAmount:
                            maxAmount != null ? String(maxAmount) : null,
                        })
                      }
                    />
                  ),
                },
              ]}
              onReset={activeFilterKeys.length > 0 ? resetFilters : undefined}
            />
          }
        />
        <ScrollTable
          className="min-h-0 w-full flex-1"
          scrollStateKey={[
            accountIds.join(','),
            allocationFilter,
            reconciledFilter,
            dateFilter.startDate ?? '',
            dateFilter.endDate ?? '',
            amountFilter.minAmount ?? '',
            amountFilter.maxAmount ?? '',
            search,
          ].join('|')}
          body={
            <>
              {/* The newer end's control rides above the first row: it is the
                  end of the list it extends, and the only place a click on it
                  doesn't scroll the rows it just loaded out of view. */}
              {canPageNewer && (
                <MoreButton
                  loading={loadingNewer}
                  hasMore
                  onMore={handleMoreNewer}
                  label={
                    pagedPastEnd || !endDate
                      ? 'More newer'
                      : `Newer than ${formatBound(endDate)}`
                  }
                />
              )}
              {bodyContent}
              {(!isEmpty || hasMore || canPageOlder) && (
                <MoreButton
                  loading={loading && !loadingNewer}
                  hasMore={hasMore || canPageOlder}
                  onMore={handleMore}
                  label={
                    !hasMore && canPageOlder && startDate
                      ? `Older than ${formatBound(startDate)}`
                      : undefined
                  }
                />
              )}
            </>
          }
        />
      </div>
    </div>
  );
};
